import { z } from 'zod'
import { MAX_BANK_DISCOUNT_MILLI } from '../helpers/sales-money.js'
import { salesRatesSchema } from './sales-price-list.dto.js'
import { salesLineGroupSchema, salesPacketKindSchema, salesPacketStatusSchema } from '../enums/sales-packet.enum.js'
import { scheduleEntrySchema, scheduleDueSchema } from '../helpers/sales-schedule.js'
import { autopayDtoSchema, EMPTY_SALES_REVENUE, salesPaymentDtoSchema, salesRevenueDtoSchema } from './sales-payment.dto.js'
import { calendarDaySchema, instantSchema, wallClockSchema } from '../validators/temporal.validator.js'

/** A packet as staff see it on the event (docs/sales.spec.md, "Sending a packet"). */

export const packetLineDtoSchema = z.object({
  id: z.string().uuid(),
  group: salesLineGroupSchema,
  itemId: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string(),
  unitCents: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  taxable: z.boolean(),
  serviceCharged: z.boolean(),
  /** A package or add-on the couple picked; an included line always is. */
  chosen: z.boolean(),
})

export const packetSignerDtoSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  signedAt: instantSchema.nullable(),
  declinedAt: instantSchema.nullable(),
})

export const salesPacketDtoSchema = z.object({
  id: z.string().uuid(),
  kind: salesPacketKindSchema,
  status: salesPacketStatusSchema,
  date: calendarDaySchema,
  /** Venue wall clock, as an event's. */
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  trackIds: z.array(z.string().uuid()),
  validUntil: calendarDaySchema,
  /** Past its valid-until at the venue: it can't be chosen, signed or paid. */
  lapsed: z.boolean(),
  rates: salesRatesSchema,
  bankDiscountMilli: z.number().int().min(0).max(MAX_BANK_DISCOUNT_MILLI),
  templateName: z.string(),
  schedule: z.array(scheduleEntrySchema),
  securityDeposit: z.object({ cents: z.number().int(), due: scheduleDueSchema }).nullable(),
  lines: z.array(packetLineDtoSchema),
  signers: z.array(packetSignerDtoSchema),
  sentBy: z.string().nullable(),
  countersigner: z.object({ userId: z.string().uuid(), name: z.string() }).nullable(),
  /** The couple have all signed and the countersigner hasn't: it's their turn. */
  countersignReady: z.boolean(),
  countersignedAt: instantSchema.nullable(),
  sentAt: instantSchema,
  closedAt: instantSchema.nullable(),
  /** A change to the agreement: the agreement it changes. Defaulted while an older API answers. */
  amendsPacketId: z.string().uuid().nullable().default(null),
  /** A change still out: what's been paid toward the agreement, which its schedule comes after. */
  paidBeforeCents: z.number().int().nullable().default(null),
})
export type SalesPacketDto = z.infer<typeof salesPacketDtoSchema>

/** An event's Sales section, for its staff: its packets, and who can sign and countersign a new one. */
export const eventSalesDtoSchema = z.object({
  eventId: z.string().uuid(),
  venueId: z.string().uuid(),
  /** Newest first. */
  packets: z.array(salesPacketDtoSchema),
  /** The event's clients who can sign: they have an email. */
  clients: z.array(z.object({ userId: z.string().uuid(), name: z.string(), email: z.string() })),
  /** Staff with Sales Edit at the venue, who can countersign. */
  countersigners: z.array(z.object({ userId: z.string().uuid(), name: z.string() })),
  /** What the signed agreements are owed, in order. Defaulted while an older API answers. */
  payments: z.array(salesPaymentDtoSchema).default([]),
  revenue: salesRevenueDtoSchema.default(EMPTY_SALES_REVENUE),
  /** The couple's saved method, while autopay is on. */
  autopay: autopayDtoSchema.nullable().default(null),
  canEdit: z.boolean(),
  /** Sales Edit with the Refunds switch: may give money back from any payment. */
  canRefund: z.boolean().default(false),
  /** The event is over, or canceled: its security deposits can be returned. */
  depositsReturnable: z.boolean().default(false),
})
export type EventSalesDto = z.infer<typeof eventSalesDtoSchema>

/** What sending answers with: the packet, and anything worth knowing that didn't stop it. */
export const sentPacketDtoSchema = z.object({
  packet: salesPacketDtoSchema,
  /** Such as another event holding the date: holds never block. */
  warnings: z.array(z.string()),
})
export type SentPacketDto = z.infer<typeof sentPacketDtoSchema>
